/**
 * Sign-in and account security rules shared by the API and every client. The API enforces them (Better Auth hooks in
 * `apps/api/src/auth/auth.ts`, the sessions routes); a client checks first only to answer before the round trip.
 */

/* ───────── passwords ───────── */

export const PASSWORD_POLICY = {
  minLength: 8,
  /** Better Auth's `maxPasswordLength`: hashing a megabyte-long "password" would be a cheap way to burn CPU. */
  maxLength: 128,
} as const;

/** Why a new password is refused. */
export type PasswordProblem = "tooShort" | "tooLong" | "common" | "personal";

/**
 * The `code` the API answers for each problem (Better Auth's error body `{ code, message }`). The two length codes are
 * Better Auth's own; the other two come from our hook. Clients map a code back with `passwordProblemOfCode`.
 */
export const PASSWORD_ERROR_CODES: Readonly<Record<PasswordProblem, string>> = {
  tooShort: "PASSWORD_TOO_SHORT",
  tooLong: "PASSWORD_TOO_LONG",
  common: "PASSWORD_TOO_COMMON",
  personal: "PASSWORD_TOO_PERSONAL",
};

export function passwordProblemOfCode(code: string | null | undefined): PasswordProblem | null {
  const hit = (Object.entries(PASSWORD_ERROR_CODES) as [PasswordProblem, string][]).find(([, c]) => c === code);
  return hit ? hit[0] : null;
}

/**
 * Passwords that appear near the top of every breached-password list and are long enough to pass the length rule
 * (NIST SP 800-63B §3.1.1.2: refuse commonly used values). Lower case; matched after lower-casing.
 */
const COMMON_PASSWORDS: ReadonlySet<string> = new Set([
  "iloveyou", "sunshine", "princess", "football", "baseball", "basketball", "superman", "starwars", "whatever",
  "trustno1", "computer", "internet", "michelle", "jennifer", "jessica1", "jordan23", "mercedes", "corvette",
  "maverick", "midnight", "butterfly", "chocolate", "beautiful", "sweetheart", "babygirl", "lovelove", "liverpool",
  "manchester", "barcelona", "spiderman", "minecraft", "fuckyou1", "fuckyou2", "qazwsxedc", "qweasdzxc", "1qaz2wsx",
  "1q2w3e4r", "1q2w3e4r5t", "1q2w3e4r5t6y", "zaq12wsx", "zaq1zaq1", "1qazxsw2", "q1w2e3r4", "q1w2e3r4t5",
  "a1b2c3d4", "1a2b3c4d", "aa123456", "qwertyqwerty", "asdfasdf", "zxcvzxcv", "passpass", "password1",
  "hellokitty", "samantha", "elizabeth", "jonathan", "victoria", "alexander", "nicholas", "christopher",
  "princesa", "estrella", "mariposa", "babygirl1", "lovely123", "letmein123", "welcome123", "changeme123",
  "goodluck", "blink182", "metallica", "mustang1", "harley01", "ranger01", "thunder1", "jasmine1", "pepper01",
  "iloveu123", "123qweasd", "qwe123qwe", "abc123abc", "test12345", "admin1234", "administrator", "adminadmin",
  "rootroot", "guest123", "passw0rd1", "p4ssw0rd", "temp1234", "default1",
]);

/**
 * Words that stay just as guessable with digits or symbols around them ("Password2026!", "welcome1", "Wedding@2027"):
 * compared with the password once the letters in its middle are pulled out and common character swaps are undone.
 * Includes this service's own vocabulary, which NIST lists as context-specific words to refuse.
 */
const COMMON_BASES: ReadonlySet<string> = new Set([
  "password", "passwort", "passord", "passwd", "pass", "qwerty", "qwertz", "azerty", "qwer", "asdf", "zxcv", "qwertyuiop",
  "asdfghjkl", "asdfgh", "zxcvbnm", "qazwsx", "letmein", "welcome", "iloveyou", "loveyou", "love", "admin", "login",
  "hello", "secret", "hemmelig", "monkey", "dragon", "master", "shadow", "sunshine", "princess", "football", "soccer",
  "baseball", "superman", "batman", "starwars", "pokemon", "michael", "jordan", "charlie", "freedom", "whatever",
  "trustno", "abc", "abcd", "abcde", "test", "changeme", "default", "summer", "winter", "spring", "autumn", "sommer",
  "vinter", "angel", "hunter", "family", "baby", "forever", "computer", "google", "facebook", "samsung", "apple",
  "iphone", "android", "norway", "norge", "oslo", "teamo", "flower", "elskerdeg", "jegelskerdeg",
  "bridewell", "wedding", "bride", "groom", "marriage", "married", "justmarried", "mrandmrs", "honeymoon",
]);

/** Sequences people type instead of a password (keyboard rows, alphabet, digits, either direction). */
const SEQUENCES = [
  "01234567890123456789", "98765432109876543210", "abcdefghijklmnopqrstuvwxyz", "zyxwvutsrqponmlkjihgfedcba",
  "qwertyuiopasdfghjklzxcvbnm", "mnbvcxzlkjhgfdsapoiuytrewq", "1qaz2wsx3edc4rfv5tgb6yhn", "1q2w3e4r5t6y7u8i9o0p",
  "qazwsxedcrfvtgbyhnujmikolp",
];

const LEET: Readonly<Record<string, string>> = { "@": "a", "4": "a", "3": "e", "1": "i", "!": "i", "0": "o", "$": "s", "5": "s", "7": "t", "+": "t" };
const undoLeet = (s: string) => [...s].map((ch) => LEET[ch] ?? ch).join("");

/** The letters in the middle of a password: digits and symbols around it stripped, character swaps undone. */
function coreWord(lower: string): string {
  return undoLeet(lower.replace(/^[^a-z@$!]+|[^a-z]+$/g, "")).replace(/[^a-z]/g, "");
}

/** One character over and over, a short unit repeated ("abcabcab", "12121212"), or a slice of a sequence above. */
function isTrivial(lower: string): boolean {
  for (let n = 1; n <= 4; n++) {
    const unit = lower.slice(0, n);
    if (lower.length >= n * 2 && [...lower].every((ch, i) => ch === unit[i % n])) return true;
  }
  return SEQUENCES.some((s) => s.includes(lower));
}

/**
 * Why `password` is not acceptable as a new password, or `null` when it is. Not a strength meter: it refuses what
 * breach lists and guessing tools try first, plus the person's own name and address (NIST SP 800-63B §3.1.1.2).
 */
export function passwordProblem(password: string, context: { email?: string | null; name?: string | null } = {}): PasswordProblem | null {
  if (password.length < PASSWORD_POLICY.minLength) return "tooShort";
  if (password.length > PASSWORD_POLICY.maxLength) return "tooLong";
  const lower = password.toLowerCase();
  const core = coreWord(lower);
  // A short all-digit password is a date or a phone number: the first thing a guesser tries.
  if (/^\d+$/.test(lower) && lower.length < 10) return "common";
  if (COMMON_PASSWORDS.has(lower) || COMMON_PASSWORDS.has(undoLeet(lower)) || isTrivial(lower)) return "common";
  if (core.length >= 3 && COMMON_BASES.has(core)) return "common";

  const email = context.email?.trim().toLowerCase() ?? "";
  const local = email.split("@")[0] ?? "";
  if (email && lower.includes(email)) return "personal";
  const localWord = local.replace(/[^a-z]/g, "");
  if (local.length >= 3 && (lower === local || (localWord.length >= 3 && core === localWord))) return "personal";
  const names = (context.name ?? "").toLowerCase().split(/[\s.,_'&-]+/).map((p) => p.replace(/[^a-z]/g, "")).filter((p) => p.length >= 3);
  const wholeName = names.join("");
  if (names.some((p) => core === p) || (wholeName.length >= 3 && core === wholeName)) return "personal";
  return null;
}

/* ───────── devices and sessions ───────── */

/**
 * Where a request comes from. `web` is a browser; the others are our own apps, which identify themselves with
 * `nativeUserAgent` (the Apple apps adopt the same format when they start talking to the API).
 */
export const CLIENT_PLATFORMS = ["web", "android", "ios", "ipados", "macos", "visionos"] as const;
export type ClientPlatform = (typeof CLIENT_PLATFORMS)[number];

const PLATFORM_OS_NAME: Readonly<Record<Exclude<ClientPlatform, "web">, string>> = {
  android: "Android", ios: "iOS", ipados: "iPadOS", macos: "macOS", visionos: "visionOS",
};

/**
 * The `User-Agent` our apps send: `Bridewell/<app version> (<OS> <OS version>; <device model>)`, e.g.
 * `Bridewell/1.0.0 (Android 15; Pixel 9)`. Sign-in sessions keep it, so the person can tell their devices apart.
 */
export function nativeUserAgent(o: { platform: Exclude<ClientPlatform, "web">; appVersion: string; osVersion?: string | number | null; model?: string | null }): string {
  const clean = (s: string) => s.replace(/[()\r\n;]/g, " ").replace(/\s+/g, " ").trim().slice(0, 60);
  const os = [PLATFORM_OS_NAME[o.platform], o.osVersion != null ? clean(String(o.osVersion)) : ""].filter(Boolean).join(" ");
  const model = o.model ? clean(o.model) : "";
  return `Bridewell/${clean(o.appVersion)} (${model ? `${os}; ${model}` : os})`;
}

/** What a session's user agent says about the device, as far as it can be read. Every field may be null. */
export interface ClientDevice {
  /** `app` = one of our apps (`nativeUserAgent`), `browser` = a web browser, `unknown` = anything else. */
  kind: "app" | "browser" | "unknown";
  platform: ClientPlatform | null;
  /** "Android 15", "iOS 18.2", "macOS", "Windows", … */
  os: string | null;
  /** "Chrome", "Safari", "Firefox", "Edge", "Samsung Internet", "Opera" — browsers only. */
  browser: string | null;
  /** The app's version — our apps only. */
  appVersion: string | null;
  /** The device model our apps report ("Pixel 9"). */
  model: string | null;
}

const BROWSERS: readonly [RegExp, string][] = [
  [/\bEdg(?:e|A|iOS)?\//, "Edge"],
  [/\bSamsungBrowser\//, "Samsung Internet"],
  [/\bOPR\/|\bOpera\b/, "Opera"],
  [/\bFirefox\/|\bFxiOS\//, "Firefox"],
  [/\bChrome\/|\bCriOS\/|\bChromium\//, "Chrome"],
  [/\bVersion\/[\d.]+.*\bSafari\//, "Safari"],
];

function osOf(ua: string): { os: string | null; platform: ClientPlatform | null } {
  const android = /\bAndroid (\d+(?:\.\d+)?)/.exec(ua);
  if (android) return { os: `Android ${android[1]}`, platform: "web" };
  const ipad = /\biPad\b.*?\bOS (\d+)[_.](\d+)/.exec(ua);
  if (ipad) return { os: `iPadOS ${ipad[1]}.${ipad[2]}`, platform: "web" };
  const ios = /\biPhone OS (\d+)[_.](\d+)/.exec(ua);
  if (ios) return { os: `iOS ${ios[1]}.${ios[2]}`, platform: "web" };
  // Browsers on a Mac freeze the version at 10_15_7, so it says nothing; the same goes for Windows 10 vs 11.
  if (/\bMacintosh\b|\bMac OS X\b/.test(ua)) return { os: "macOS", platform: "web" };
  if (/\bWindows\b/.test(ua)) return { os: "Windows", platform: "web" };
  if (/\bCrOS\b/.test(ua)) return { os: "ChromeOS", platform: "web" };
  if (/\bLinux\b/.test(ua)) return { os: "Linux", platform: "web" };
  return { os: null, platform: null };
}

/** Reads a stored or incoming `User-Agent` (`nativeUserAgent` for our apps, a browser string otherwise). */
export function describeUserAgent(raw: string | null | undefined): ClientDevice {
  const ua = (raw ?? "").slice(0, 512);
  const unknown: ClientDevice = { kind: "unknown", platform: null, os: null, browser: null, appVersion: null, model: null };
  if (!ua) return unknown;
  const app = /^Bridewell\/([\w.+-]{1,40}) \(([^;)]*)(?:; ([^)]*))?\)/.exec(ua);
  if (app) {
    const os = app[2]!.trim();
    const platform = (Object.entries(PLATFORM_OS_NAME) as [Exclude<ClientPlatform, "web">, string][]).find(([, name]) => os === name || os.startsWith(`${name} `))?.[0] ?? null;
    return { kind: "app", platform, os: os || null, browser: null, appVersion: app[1]!, model: app[3]?.trim() || null };
  }
  const browser = BROWSERS.find(([re]) => re.test(ua))?.[1] ?? null;
  const { os, platform } = osOf(ua);
  if (!browser) return { ...unknown, os };
  return { kind: "browser", platform, os, browser, appVersion: null, model: null };
}

/** One sign-in session of the signed-in person (`GET /me/sessions`). Never carries the session token. */
export interface SessionDTO {
  id: string;
  /** The session this request was made with ("this device"). */
  current: boolean;
  createdAt: string;
  /** When the session was last refreshed; Better Auth refreshes at most once a day (`session.updateAge`). */
  lastActiveAt: string;
  expiresAt: string;
  device: ClientDevice;
}
